/**
 * Automatic Streak Types for Frontend
 */
import { DecimalSchema } from "../../decimal/decimal.schema";
import { AUTOMATIC_STREAK_STATUS, AUTOMATIC_STREAK_STOP_REASON } from "./config";
import z from "zod";

// Trade execution log entry
export const TradeExecutionLogEntrySchema = z.object({
  index: z.number().int().min(0),
  buyOrderId: z.string().optional(),
  sellOrderId: z.string().optional(),
  volume: DecimalSchema,
  timestamp: z.coerce.date(),
  error: z.string().optional(),
  success: z.boolean(),
});

export type TradeExecutionLogEntry = z.infer<typeof TradeExecutionLogEntrySchema>;

export const ExecutionLogSchema = z.object({
  trades: z.array(TradeExecutionLogEntrySchema),
  failures: z.array(z.string()),
  scheduledCycles: z.array(z.any()).optional(),
  spreadThresholdPercent: z.number().optional(),
});
export type ExecutionLog = z.infer<typeof ExecutionLogSchema>;

/**
 * Get the error message from the last failed trade in the execution log.
 */
export const getLastTradeError = (executionLog?: ExecutionLog): string | undefined => {
  if (!executionLog?.trades) return undefined;
  const failedTrade = executionLog.trades.find((t) => !t.success);
  return failedTrade?.error;
};

/**
 * Get the index of the last failed trade (1-based for display).
 */
export const getFailedTradeIndex = (executionLog?: ExecutionLog): number | undefined => {
  if (!executionLog?.trades) return undefined;
  const failedTrade = executionLog.trades.find((t) => !t.success);
  return failedTrade ? failedTrade.index + 1 : undefined;
};

// Request DTOs (V1)
export interface CreateStreakRequest {
  campaignName: string;
  epochId: number;
  exchangeAccountId: number;
  pairSymbol: string;
  totalTrades: number;
  volumePerCycle: string; // Single volume input - backend calculates min/max using variance
  intervalSeconds: number;
  scheduledStartTime: string;
  alsoRunForNextEpoch?: boolean; // Create another streak for next epoch with same config
}

export interface GetUserStreaksRequest {
  campaignName?: string;
}

export const CreateStreakResponseSchema = z.object({
  streakId: z.string(),
  status: z.string(),
  /** V1 only - V2 calculates this server-side */
  scheduledStartTime: z.string().optional(),
});
export type CreateStreakResponse = z.infer<typeof CreateStreakResponseSchema>;

export const StreakResponseSchema = z.object({
  id: z.string(),
  hubUserId: z.number(),
  campaignName: z.string(),
  epochId: z.number(),
  exchangeAccountId: z.number(),
  pairSymbol: z.string(),
  totalTrades: z.number(),
  volumeMin: z.string(),
  volumeMax: z.string(),
  intervalSeconds: z.number(),
  scheduledStartTime: z.string(),
  actualStartTime: z.string().optional(),
  status: z.enum(AUTOMATIC_STREAK_STATUS),
  completedTrades: z.number(),
  skippedCycles: z.number(), // Cycles skipped due to spread threshold
  totalVolumeTraded: z.string(),
  executionLog: ExecutionLogSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  finishedAt: z.string().optional(),
  stopReason: z.enum(AUTOMATIC_STREAK_STOP_REASON).optional(),
});

export type StreakResponse = z.infer<typeof StreakResponseSchema>;

/**
 * @deprecated Use StreakResponse instead
 */
export type StreakStatusResponse = StreakResponse;

export const StreakSummaryResponseSchema = z.object({
  id: z.string(),
  campaignName: z.string(),
  epochId: z.number(),
  pairSymbol: z.string(),
  totalTrades: z.number(),
  completedTrades: z.number(),
  skippedCycles: z.number().optional(), // Cycles skipped due to spread threshold or volume cooldown
  status: z.enum(AUTOMATIC_STREAK_STATUS),
  scheduledStartTime: z.string(),
  actualStartTime: z.string().optional(),
  finishedAt: z.string().optional(),
  stopReason: z.enum(AUTOMATIC_STREAK_STOP_REASON).optional(),
  totalVolumeTraded: z.string(),
  createdAt: z.string(),
});

export type StreakSummaryResponse = z.infer<typeof StreakSummaryResponseSchema>;

export const CancelStreakResponseSchema = z.object({
  streakId: z.string(),
  status: z.string(),
});
export type CancelStreakResponse = z.infer<typeof CancelStreakResponseSchema>;

// SSE Progress Event
export interface AutomaticStreakProgressEvent {
  hubUserId: number;
  streakId: string;
  status: string;
  completedTrades: number;
  skippedCycles: number; // Cycles skipped due to spread threshold
  totalTrades: number;
  totalVolumeTraded: string;
  message: string;
  stopReason?: string;
  correlationId: string;
  timestamp: number;
}

// Global configuration (from database)
export const AutomaticStreakConfigSchema = z.object({
  enabled: z.boolean(), // Replaces GrowthBook feature flags
  minTrades: z.number(),
  maxTrades: z.number(),
  minVolumePerTrade: z.number(),
  maxVolumePerTrade: z.number(),
  minVolumeDifference: z.number(), // Minimum difference between volumeMin and volumeMax
  validIntervals: z.array(z.number()),
  defaultStartDelayMinutes: z.number(), // Default delay in minutes before autopilot starts (0 = instant)
  defaultCycles: z.number(), // Default number of cycles shown in form
  volumeVariancePercent: z.number(), // Variance percentage for single volume input (±25% by default)
  defaultVolumePerTrade: z.number(), // Default volume shown in autopilot form
});
export type AutomaticStreakConfig = z.infer<typeof AutomaticStreakConfigSchema>;

export const AutomaticStreakConfigResponseSchema = z.object({
  config: AutomaticStreakConfigSchema,
});
export type AutomaticStreakConfigResponse = z.infer<typeof AutomaticStreakConfigResponseSchema>;

export const AutomaticStreakCguStatusResponseSchema = z.object({
  acknowledged: z.boolean(),
});
export type AutomaticStreakCguStatusResponse = z.infer<typeof AutomaticStreakCguStatusResponseSchema>;

// Minimum required balance endpoint types
export const MinimumRequiredBalanceRequestSchema = z.object({
  campaignName: z.string(),
  epochId: z.number().int(),
  exchangeAccountId: z.number().int(),
  totalTargetVolume: z.string(), // String representation of Decimal for frontend
});

export type MinimumRequiredBalanceRequest = z.infer<typeof MinimumRequiredBalanceRequestSchema>;

export const MinimumRequiredBalanceResponseSchema = z.object({
  minimumRequiredBalance: z.number(),
});

export type MinimumRequiredBalanceResponse = z.infer<typeof MinimumRequiredBalanceResponseSchema>;

// Export V2 types
export * from "./automatic-streak-v2";
export * from "./config";
